import {ReactNode} from 'react'
import {getCurrentColors} from '../../utils/colors'

export const COLORS = getCurrentColors()

function _escRe(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** Realça (negrito/destaque) as palavras do termo dentro de um texto. */
export function realcar(texto: string, termo: string): ReactNode {
    const tokens = (termo || '').split(/\s+/).filter((t) => t.length > 2).map(_escRe)
    if (!tokens.length || !texto) return texto
    const partes = texto.split(new RegExp(`(${tokens.join('|')})`, 'gi'))
    return partes.map((p, i) => (i % 2 === 1 ? <mark key={i} className="mark">{p}</mark> : p))
}

export const ESFERA_LABEL: Record<string, string> = {
    F: 'Federal', E: 'Estadual', M: 'Municipal', D: 'Distrital', '?': 'Desconhecida',
}

/** Rótulos legíveis dos requisitos (o id snake_case nunca aparece p/ o usuário). */
const ROTULO_REQUISITO: Record<string, string> = {
    motivacao_necessidade: 'Motivação e necessidade',
    previsao_pca: 'Previsão no PCA/planejamento',
    vantajosidade: 'Vantajosidade (pesquisa de preços)',
    limite_individual_50: 'Limite individual (50%)',
    limite_coletivo_200: 'Limite coletivo (200%)',
    anuencia_gerenciador: 'Anuência do órgão gerenciador',
    aceite_fornecedor: 'Aceite do fornecedor',
    edital_preve_adesao: 'Edital prevê adesão',
    vigencia_ata: 'Vigência da ata',
    compatibilidade_esfera: 'Compatibilidade de esfera',
    controle_previo_publicidade: 'Controle prévio e publicidade',
}

export function rotuloRequisito(id: string): string {
    if (ROTULO_REQUISITO[id]) return ROTULO_REQUISITO[id]
    const limpo = (id || '').replace(/_/g, ' ').trim()
    return limpo ? limpo.charAt(0).toUpperCase() + limpo.slice(1) : 'Requisito'
}

/** Rótulo legível do status da justificativa (sem snake_case p/ o usuário). */
const ROTULO_STATUS: Record<string, string> = {
    APTA: 'Apta', COM_RISCO: 'Com risco', ERRO: 'Erro', PROCESSANDO: 'Processando',
}

export function rotuloStatus(s: string): string {
    return ROTULO_STATUS[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, ' ') : '—')
}

/** Cores semânticas (espelham os tokens do carona.css). */
export const FEEDBACK = {
    success: '#1d9e75', successSoft: '#e1f5ee',
    warning: '#d97706', warningSoft: '#fef3c7',
    danger: '#b31417', dangerSoft: '#fcebeb',
    info: '#3394ef', infoSoft: '#e6f1fb',
    muted: '#8a9aa8', mutedSoft: '#eef1f4',
}

export function fmtBRL(n: number | null | undefined): string {
    if (n === null || n === undefined || isNaN(n)) return '—'
    return 'R$ ' + Number(n).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})
}

export function fmtDate(iso: string | null | undefined): string {
    if (!iso) return '—'
    const d = new Date(iso + 'T00:00:00')
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR')
}

/** Backend selo -> classe/rótulo do protótipo. */
export function seloInfo(selo: string, rotulo?: string): {cls: string; label: string; key: string} {
    switch (selo) {
        case 'ADESAO_PERMITIDA': return {cls: 'selo--provavel', label: rotulo || 'Adesão permitida', key: 'provavel'}
        case 'ADESAO_CONDICIONAL': return {cls: 'selo--condicional', label: rotulo || 'Adesão condicional', key: 'condicional'}
        case 'ADESAO_VEDADA': return {cls: 'selo--vedada', label: rotulo || 'Adesão vedada', key: 'vedada'}
        case 'ATA_VENCIDA': return {cls: 'selo--vencida', label: rotulo || 'Ata vencida', key: 'vencida'}
        default: return {cls: 'selo--vencida', label: rotulo || selo, key: 'outro'}
    }
}

/** SVG paths (inner) dos ícones de alerta do protótipo. */
export const ALERT_ICONS: Record<string, string> = {
    clock: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
    ban: '<circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>',
    help: '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
    gauge: '<path d="M12 14 4 6"/><path d="M12 22a10 10 0 1 0-9.95-9"/>',
}

const SEV_CHIP: Record<string, string> = {
    BLOQUEANTE: 'alert-chip--danger', ALTA: 'alert-chip--danger',
    MEDIA: 'alert-chip--warn', BAIXA: 'alert-chip--info',
}
const COD_ICON: Record<string, string> = {
    VENCENDO: 'clock', VEDACAO_ESFERA: 'ban', CONFIRMAR_ADESAO: 'help', SALDO_INDETERMINADO: 'gauge',
}

export function alertChip(a: {codigo: string; mensagem: string; severidade: string}): {cls: string; icon: string; txt: string} {
    return {
        cls: SEV_CHIP[a.severidade] || 'alert-chip--muted',
        icon: COD_ICON[a.codigo] || 'help',
        txt: a.mensagem,
    }
}
